/**
 * Unified Dashboard Configuration
 * ================================
 * Industry configurations, role KPIs, and task templates
 * Extracted from UnifiedDashboard.jsx for better maintainability
 */

import {
  Utensils, ShoppingBag, Heart, Briefcase, Dumbbell, Home, Car,
  GraduationCap, Factory, Music, Laptop, Landmark, Wheat, Truck,
  Wrench, Scale, Hotel, Gamepad2, Leaf, Building2, Sparkles, Package,
  HeartHandshake, Pill, Printer, Bug, Users, Flame
} from 'lucide-react';

// Industry configurations with KPIs and metrics
export const INDUSTRY_CONFIG = {
  hospitality: {
    name: 'Hospitality',
    icon: Utensils,
    color: 'orange',
    gradient: 'from-orange-500 to-amber-500',
    kpis: ['Daily Revenue', 'Food Cost %', 'Labour Cost %', 'Table Turnover', 'Avg Check', 'Reviews Score'],
    metrics: ['Covers', 'Reservations', 'Walk-ins', 'No-shows'],
    benchmarks: { foodCost: 28, labourCost: 32, tableTurnover: 2.5, avgCheck: 65 }
  },
  qsr: {
    name: 'QSR',
    icon: Flame,
    color: 'amber',
    gradient: 'from-amber-600 to-orange-500',
    kpis: ['SPLH', 'Daily Revenue', 'Food Cost %', 'Labour Cost %', 'Drive-Thru Wait', 'Order Accuracy'],
    metrics: ['Transactions', 'Drive-Thru Cars', 'Avg Check', 'Delivery Orders'],
    benchmarks: { splh: 65, foodCost: 32, labourCost: 25, driveThruWait: 3.5, avgCheck: 18.50 }
  },
  retail: {
    name: 'Retail',
    icon: ShoppingBag,
    color: 'blue',
    gradient: 'from-blue-500 to-indigo-500',
    kpis: ['Daily Sales', 'Conversion Rate', 'Avg Transaction', 'Units Per Sale', 'Shrinkage', 'Foot Traffic'],
    metrics: ['Customers', 'Returns', 'Exchanges', 'Loyalty Sign-ups'],
    benchmarks: { conversion: 25, avgTransaction: 85, unitsPerSale: 2.3, shrinkage: 1.5 }
  },
  healthcare: {
    name: 'Healthcare',
    icon: Heart,
    color: 'pink',
    gradient: 'from-pink-500 to-rose-500',
    kpis: ['Daily Billings', 'Patient Satisfaction', 'Wait Time', 'Rebooking Rate', 'Utilization', 'No-show Rate'],
    metrics: ['Appointments', 'New Patients', 'Follow-ups', 'Cancellations'],
    benchmarks: { satisfaction: 4.5, waitTime: 12, rebooking: 75, utilization: 85 }
  },
  professional: {
    name: 'Professional Services',
    icon: Briefcase,
    color: 'indigo',
    gradient: 'from-indigo-500 to-purple-500',
    kpis: ['Billable Hours', 'Realization Rate', 'Utilization', 'WIP Value', 'AR Days', 'Client Retention'],
    metrics: ['Projects', 'Active Clients', 'Proposals', 'Meetings'],
    benchmarks: { utilization: 75, realization: 90, arDays: 45, retention: 85 }
  },
  fitness: {
    name: 'Fitness',
    icon: Dumbbell,
    color: 'green',
    gradient: 'from-green-500 to-emerald-500',
    kpis: ['Active Members', 'Check-ins Today', 'Class Attendance', 'PT Sessions', 'Retention Rate', 'NPS Score'],
    metrics: ['New Sign-ups', 'Cancellations', 'Frozen', 'PT Bookings'],
    benchmarks: { checkInRate: 35, classAttendance: 75, retention: 80, nps: 50 }
  },
  property: {
    name: 'Property',
    icon: Home,
    color: 'amber',
    gradient: 'from-amber-500 to-orange-500',
    kpis: ['Listings Active', 'Days on Market', 'Avg Sale Price', 'Commission YTD', 'Appraisals', 'Conversion Rate'],
    metrics: ['Open Homes', 'Inspections', 'Offers', 'Settlements'],
    benchmarks: { daysOnMarket: 35, conversion: 15, appraisalsPerWeek: 5, listingsPerMonth: 8 }
  },
  construction: {
    name: 'Construction',
    icon: Building2,
    color: 'yellow',
    gradient: 'from-yellow-500 to-amber-500',
    kpis: ['Projects Active', 'On-time Delivery', 'Budget Variance', 'Safety Incidents', 'Defect Rate', 'Client Satisfaction'],
    metrics: ['Quotes Pending', 'Workers On-site', 'Subcontractors', 'Inspections'],
    benchmarks: { onTime: 85, budgetVariance: 5, defectRate: 2, satisfaction: 4.3 }
  },
  automotive: {
    name: 'Automotive',
    icon: Car,
    color: 'red',
    gradient: 'from-red-500 to-rose-500',
    kpis: ['Vehicles Sold', 'Service Revenue', 'Parts Margin', 'Finance Penetration', 'CSI Score', 'Warranty Claims'],
    metrics: ['Test Drives', 'Service Bookings', 'Trade-ins', 'Leads'],
    benchmarks: { csiScore: 90, financePenetration: 60, partsMargin: 35, serviceRetention: 55 }
  },
  education: {
    name: 'Education',
    icon: GraduationCap,
    color: 'purple',
    gradient: 'from-purple-500 to-violet-500',
    kpis: ['Enrollment Rate', 'Attendance', 'Completion Rate', 'Student Satisfaction', 'Revenue Per Student', 'Referral Rate'],
    metrics: ['New Enrollments', 'Classes Today', 'Assessments', 'Graduations'],
    benchmarks: { attendance: 92, completion: 85, satisfaction: 4.5, referral: 30 }
  },
  beauty: {
    name: 'Beauty',
    icon: Sparkles,
    color: 'rose',
    gradient: 'from-rose-500 to-pink-500',
    kpis: ['Daily Revenue', 'Avg Service Value', 'Rebooking Rate', 'Product Sales', 'Utilization', 'Reviews Score'],
    metrics: ['Appointments', 'Walk-ins', 'No-shows', 'New Clients'],
    benchmarks: { rebooking: 65, productSales: 20, utilization: 75, reviews: 4.8 }
  },
  manufacturing: {
    name: 'Manufacturing',
    icon: Factory,
    color: 'slate',
    gradient: 'from-slate-500 to-gray-500',
    kpis: ['Units Produced', 'OEE %', 'Defect Rate', 'On-time Delivery', 'Downtime Hours', 'Cost Per Unit'],
    metrics: ['Orders Pending', 'Raw Materials', 'WIP', 'Shipments'],
    benchmarks: { oee: 85, defectRate: 1, onTime: 95, downtime: 5 }
  },
  entertainment: {
    name: 'Entertainment',
    icon: Music,
    color: 'sky',
    gradient: 'from-sky-500 to-[#007AFF]',
    kpis: ['Ticket Sales', 'Attendance', 'F&B Revenue', 'Avg Spend', 'Customer Rating', 'Social Engagement'],
    metrics: ['Events This Week', 'Pre-sales', 'Refunds', 'VIP Bookings'],
    benchmarks: { attendance: 85, avgSpend: 45, rating: 4.5, engagement: 5 }
  },
  technology: {
    name: 'Technology',
    icon: Laptop,
    color: 'cyan',
    gradient: 'from-[#007AFF] to-[#0066CC]',
    kpis: ['MRR', 'Churn Rate', 'NPS Score', 'CAC', 'LTV', 'Active Users'],
    metrics: ['New Signups', 'Trials', 'Upgrades', 'Support Tickets'],
    benchmarks: { churn: 5, nps: 45, ltv: 2400, cac: 400 }
  },
  finance: {
    name: 'Finance',
    icon: Landmark,
    color: 'emerald',
    gradient: 'from-emerald-500 to-green-500',
    kpis: ['AUM', 'Net Inflows', 'Client Retention', 'Fee Revenue', 'Compliance Score', 'Client Satisfaction'],
    metrics: ['New Clients', 'Reviews Due', 'Applications', 'Settlements'],
    benchmarks: { retention: 95, compliance: 98, satisfaction: 4.5, netInflows: 5 }
  },
  accounting: {
    name: 'Accounting',
    icon: Landmark,
    color: 'emerald',
    gradient: 'from-emerald-500 to-[#0066CC]',
    kpis: ['Billable Hours', 'Realization Rate', 'Client Retention', 'AR Days', 'Revenue Per Client', 'Tax Returns Filed'],
    metrics: ['Active Clients', 'Returns Due', 'Consultations', 'Filings'],
    benchmarks: { realization: 90, retention: 92, arDays: 35, utilization: 75 }
  },
  agriculture: {
    name: 'Agriculture',
    icon: Wheat,
    color: 'lime',
    gradient: 'from-lime-500 to-green-500',
    kpis: ['Yield Per Hectare', 'Cost Per Tonne', 'Weather Impact', 'Equipment Utilization', 'Water Usage', 'Crop Health'],
    metrics: ['Hectares Active', 'Harvest Progress', 'Orders', 'Deliveries'],
    benchmarks: { yield: 85, costPerTonne: 250, equipUtil: 75, waterEff: 90 }
  },
  logistics: {
    name: 'Logistics',
    icon: Truck,
    color: 'cyan',
    gradient: 'from-[#007AFF] to-blue-500',
    kpis: ['On-time Delivery', 'Cost Per Delivery', 'Fleet Utilization', 'Damage Rate', 'Customer Rating', 'Fill Rate'],
    metrics: ['Deliveries Today', 'Pickups', 'In Transit', 'Delayed'],
    benchmarks: { onTime: 95, fleetUtil: 85, damageRate: 0.5, fillRate: 98 }
  },
  trades: {
    name: 'Trades',
    icon: Wrench,
    color: 'orange',
    gradient: 'from-orange-500 to-red-500',
    kpis: ['Jobs Completed', 'Avg Job Value', 'First-time Fix', 'Call-back Rate', 'Quote Conversion', 'Customer Rating'],
    metrics: ['Jobs Today', 'Quotes Pending', 'Parts on Order', 'Callbacks'],
    benchmarks: { firstTimeFix: 85, callbackRate: 5, quoteConversion: 40, rating: 4.7 }
  },
  legal: {
    name: 'Legal',
    icon: Scale,
    color: 'slate',
    gradient: 'from-slate-600 to-gray-600',
    kpis: ['Billable Hours', 'Realization Rate', 'Matter Pipeline', 'AR Days', 'Win Rate', 'Client Satisfaction'],
    metrics: ['Active Matters', 'Court Dates', 'Filings Due', 'Consultations'],
    benchmarks: { realization: 90, arDays: 60, winRate: 75, satisfaction: 4.5 }
  },
  accommodation: {
    name: 'Accommodation',
    icon: Hotel,
    color: 'teal',
    gradient: 'from-[#0066CC] to-[#007AFF]',
    kpis: ['Occupancy Rate', 'ADR', 'RevPAR', 'Guest Rating', 'Direct Bookings', 'F&B Revenue'],
    metrics: ['Check-ins Today', 'Check-outs', 'Reservations', 'Cancellations'],
    benchmarks: { occupancy: 75, adr: 180, guestRating: 4.5, directBookings: 35 }
  },
  gaming: {
    name: 'Gaming',
    icon: Gamepad2,
    color: 'fuchsia',
    gradient: 'from-fuchsia-500 to-purple-500',
    kpis: ['Net Gaming Revenue', 'Turnover', 'Hold %', 'Active Players', 'Loyalty Redemptions', 'Compliance Score'],
    metrics: ['Players Today', 'Jackpots', 'Promotions Active', 'Incidents'],
    benchmarks: { hold: 12, activePlayer: 450, loyaltyRedemption: 25, compliance: 100 }
  },
  environmental: {
    name: 'Environmental',
    icon: Leaf,
    color: 'green',
    gradient: 'from-green-500 to-emerald-500',
    kpis: ['Carbon Offset', 'Waste Diverted', 'Energy Saved', 'Projects Active', 'Client Impact', 'Compliance Rate'],
    metrics: ['Assessments', 'Reports Due', 'Certifications', 'Audits'],
    benchmarks: { wasteDiversion: 85, energySaved: 20, compliance: 95, clientImpact: 15 }
  },
  // Franchise Industries
  cleaning: {
    name: 'Cleaning Services',
    icon: Sparkles,
    color: 'sky',
    gradient: 'from-sky-500 to-[#007AFF]',
    kpis: ['Active Contracts', 'MRR', 'Contract Retention', 'Quality Score', 'Labor Cost %', 'Client Satisfaction'],
    metrics: ['Jobs Today', 'Inspections', 'New Contracts', 'Callbacks'],
    benchmarks: { retention: 90, laborCost: 55, qualityScore: 95, satisfaction: 4.5 }
  },
  pet_services: {
    name: 'Pet Services',
    icon: Heart,
    color: 'amber',
    gradient: 'from-amber-500 to-yellow-500',
    kpis: ['Daily Revenue', 'Occupancy Rate', 'Rebooking Rate', 'Customer Rating', 'Add-on Rate', 'Incident Rate'],
    metrics: ['Pets Today', 'Grooming Appts', 'Boarding Guests', 'Daycare Check-ins'],
    benchmarks: { occupancy: 75, rebooking: 75, rating: 4.8, incidentRate: 2 }
  },
  childcare: {
    name: 'Childcare',
    icon: Users,
    color: 'pink',
    gradient: 'from-pink-500 to-rose-500',
    kpis: ['Occupancy Rate', 'Daily Fee', 'Staff Ratio', 'Quality Rating', 'Parent Satisfaction', 'Waitlist Conversion'],
    metrics: ['Children Today', 'Staff On Duty', 'Waitlist Size', 'Tours Booked'],
    benchmarks: { occupancy: 90, staffRatio: 4, satisfaction: 4.5, nqsRating: 4 }
  },
  senior_care: {
    name: 'Senior Care',
    icon: HeartHandshake,
    color: 'rose',
    gradient: 'from-rose-500 to-red-500',
    kpis: ['Occupancy Rate', 'Care Hours', 'Staff Ratio', 'Compliance Score', 'Family Satisfaction', 'Incident Rate'],
    metrics: ['Residents', 'Staff On Duty', 'Care Reviews', 'Family Visits'],
    benchmarks: { occupancy: 92, compliance: 98, satisfaction: 4.3, incidentRate: 5 }
  },
  pharmacy: {
    name: 'Pharmacy',
    icon: Pill,
    color: 'blue',
    gradient: 'from-blue-500 to-indigo-500',
    kpis: ['Scripts Per Day', 'Script Margin', 'Front Shop Sales', 'Wait Time', 'Customer Retention', 'Professional Services'],
    metrics: ['Scripts Today', 'Consultations', 'Deliveries', 'Special Orders'],
    benchmarks: { scriptsPerDay: 200, frontShopPercent: 35, waitTime: 10, retention: 80 }
  },
  staffing: {
    name: 'Staffing',
    icon: Users,
    color: 'indigo',
    gradient: 'from-indigo-500 to-violet-500',
    kpis: ['Placements MTD', 'Fill Rate', 'Time to Fill', 'Gross Margin', 'Client Retention', 'Candidate Rating'],
    metrics: ['Open Jobs', 'Candidates Active', 'Interviews Today', 'Offers Pending'],
    benchmarks: { fillRate: 70, timeToFill: 20, margin: 22, retention: 85 }
  },
  printing: {
    name: 'Printing',
    icon: Printer,
    color: 'purple',
    gradient: 'from-purple-500 to-violet-500',
    kpis: ['Monthly Revenue', 'Gross Margin', 'Equipment Utilization', 'Turnaround Time', 'Repeat Rate', 'Online Orders'],
    metrics: ['Jobs In Progress', 'Quotes Pending', 'Deliveries', 'Proofs Awaiting'],
    benchmarks: { margin: 45, utilization: 70, turnaround: 3, repeatRate: 75 }
  },
  pest_control: {
    name: 'Pest Control',
    icon: Bug,
    color: 'lime',
    gradient: 'from-lime-500 to-green-500',
    kpis: ['Jobs Per Tech', 'Avg Job Value', 'Recurring Revenue %', 'First Call Resolution', 'Customer Rating', 'Callback Rate'],
    metrics: ['Jobs Today', 'Callbacks', 'New Contracts', 'Inspections'],
    benchmarks: { jobsPerTech: 6, recurringPercent: 60, resolution: 90, callbackRate: 3 }
  },
  moving_storage: {
    name: 'Moving & Storage',
    icon: Package,
    color: 'orange',
    gradient: 'from-orange-500 to-amber-500',
    kpis: ['Moves Per Week', 'Avg Move Value', 'Storage Occupancy', 'On-time Rate', 'Damage Rate', 'Customer Rating'],
    metrics: ['Moves Today', 'Quotes Pending', 'Storage Units', 'Claims Open'],
    benchmarks: { occupancy: 85, onTime: 95, damageRate: 1, rating: 4.5 }
  }
};

// Role-based KPIs with industry context
export const ROLE_KPIS = {
  owner: ['Monthly Revenue', 'Net Profit', 'Customer Satisfaction', 'Staff Efficiency'],
  'venue-operator': ['Monthly Revenue', 'Net Profit', 'Customer Satisfaction', 'Staff Efficiency'],
  manager: ['Team Performance', 'Sales Target', 'Customer Feedback', 'Operational Costs'],
  staff: ['Personal Sales', 'Tasks Completed', 'Customer Rating', 'Shift Hours'],
  accountant: ['AR Days', 'Cash Flow', 'Expense Ratio', 'Budget Variance'],
  marketing: ['Campaign ROI', 'Lead Generation', 'Conversion Rate', 'Brand Awareness']
};

// Get industry icon component
export const getIndustryIcon = (industryKey) => {
  return INDUSTRY_CONFIG[industryKey]?.icon || Briefcase;
};

// Get industry color
export const getIndustryColor = (industryKey) => {
  return INDUSTRY_CONFIG[industryKey]?.color || 'slate';
};

// Get industry gradient
export const getIndustryGradient = (industryKey) => {
  return INDUSTRY_CONFIG[industryKey]?.gradient || 'from-slate-500 to-gray-500';
};

// Get all industry keys
export const getIndustryKeys = () => Object.keys(INDUSTRY_CONFIG);

// Get industry by key
// Industries without a dedicated INDUSTRY_CONFIG entry resolve to the closest
// configured one (mirrors the role/demo-data aliases elsewhere) so the dashboard
// header/KPIs never fall back to an unrelated industry (e.g. hospitality).
const INDUSTRY_CONFIG_ALIASES = {
  it_services: 'technology',
  saas: 'technology',
  software: 'technology',
  media_telecom: 'technology',
  media: 'technology',
  wholesale: 'retail',
  mining: 'manufacturing',
  utilities: 'manufacturing',
  admin_services: 'professional',
  public_admin: 'professional',
};

export const getIndustryConfig = (industryKey) => {
  const key = (industryKey || '').toLowerCase();
  return INDUSTRY_CONFIG[key] || INDUSTRY_CONFIG[INDUSTRY_CONFIG_ALIASES[key]] || INDUSTRY_CONFIG.professional;
};
